import classNames from 'classnames';

import MarkdownComponent from 'containers/user/learn/components/markdown/markdown';
import ErrorWidget from 'containers/user/home/components/widgets/error-widget';
import NavButton from 'containers/user/home/components/shared/navigation-button';

import { formatDate } from 'helpers/dates-formatter';

interface ReleaseNotesConfig {
  colSpan: number;
  rowSpan: Record<string, number>;
}

export const getReleaseNotesConfig = (withImage: boolean): ReleaseNotesConfig =>
  withImage
    ? {
        colSpan: 2,
        rowSpan: {
          default: 9,
          mobile: 14,
        },
      }
    : {
        colSpan: 1,
        rowSpan: {
          default: 9,
        },
      };

interface SkeletonLoaderProps {
  withImage?: boolean;
}

const SkeletonLoader = ({
  withImage,
}: SkeletonLoaderProps): React.JSX.Element => (
  <div
    role="progressbar"
    className={classNames(
      'h-full w-full p-5 lg:py-3 bg-gray-100 rounded-lg animate-pulse flex',
      withImage ? 'flex-row gap-x-5' : 'flex-col',
    )}
  >
    {!withImage && <div className="h-full w-1/2 bg-gray-300 rounded"></div>}
    <div className="flex-1 flex flex-col gap-y-2">
      <div className="h-6 w-1/4 bg-gray-300 rounded mb-2"></div>
      <div className="h-4 w-3/4 bg-gray-300 rounded"></div>
      <div className="h-4 w-full bg-gray-300 rounded"></div>
      <div className="h-4 w-2/3 bg-gray-300 rounded"></div>
      <div className="mt-auto h-8 w-32 bg-gray-300 rounded"></div>
    </div>
  </div>
);

interface ReleaseNotesAttributes {
  releaseNotesDate?: string;
  image?: {
    imageSrc: { data: { attributes: { url: string } } };
  };
  releaseNotes?: string;
  targetLink?: string;
  title?: string;
}

interface ReleaseNotesData {
  attributes?: ReleaseNotesAttributes;
}

interface ReleaseNotesProps {
  data?: ReleaseNotesData;
  loading?: boolean;
  error?: unknown;
  hasImage?: boolean;
}

const ReleaseNotes = ({
  data,
  loading,
  error,
  hasImage,
}: ReleaseNotesProps): React.JSX.Element => {
  if (loading) {
    return <SkeletonLoader withImage={hasImage} />;
  }

  const {
    releaseNotesDate,
    image: releaseImage,
    releaseNotes,
    targetLink,
    title,
  } = data?.attributes || {};

  if (error || !releaseNotes) {
    return (
      <div className="p-5 pt-4 rounded-lg h-full w-full flex flex-col justify-center border border-navy-200">
        <h3 className="font-bold text-xl text-navy-800 mb-2.5">{title}</h3>
        <ErrorWidget />
      </div>
    );
  }

  return (
    <div className="p-5 pt-4 bg-blue-100 rounded-lg h-full w-full flex flex-col justify-between">
      <div className="flex items-start flex-wrap">
        <div className="flex items-center justify-between w-full mb-2.5">
          <h3 className="font-bold text-xl text-navy-800">{title}</h3>
          <p className="font-sm font-semibold text-navy-500">
            {formatDate(releaseNotesDate, 'MMMM D, YYYY')}
          </p>
        </div>

        {releaseImage && (
          <img
            src={releaseImage.imageSrc.data.attributes.url}
            alt="Release Notes"
            className=" rounded-md w-[285px] h-[194px] mr-4 mb-2"
          />
        )}

        <div className="flex-1 flex flex-col gap-2">
          <div className="text-navy-800">
            <MarkdownComponent text={releaseNotes} />
          </div>
        </div>
      </div>
      <NavButton link={targetLink} text="Read more" />
    </div>
  );
};

export default ReleaseNotes;
